import { useCallback } from 'react';
import { Button, Helper } from '@treezor/design-system';

import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/ui-kit/Tips/InfoTooltip';

import { ICreateUserInfo, ITaxResidenceCreate } from '@/features/user/contexts/CreateUserContext';

import { defaultTaxResidence } from './CreateUserTaxResidences';
import { BooleanRadio } from './BooleanRadio';

interface IUSResident {
  title: string;
  description: string;
  value: string;
  handleValuesChange: (changes: { value: string | ITaxResidenceCreate[]; key: keyof ICreateUserInfo }[]) => void;
  taxResidences?: ITaxResidenceCreate[];
  shouldShowWarning?: boolean;
}

export function USResident({
  title,
  description,
  value,
  taxResidences,
  handleValuesChange,
  shouldShowWarning = false,
}: IUSResident) {
  const { t } = useTranslation();

  const handleUSResidence = useCallback(
    (value: string, key: keyof ICreateUserInfo) => {
      // Add US Tax residence if there isn't any
      if (value === '1') {
        if (!taxResidences?.find((taxResidence: ITaxResidenceCreate) => taxResidence.country === 'US')) {
          const newTaxResidences = [
            ...(taxResidences ?? []).filter((taxResidence: ITaxResidenceCreate) => taxResidence.country !== null),
            { ...(taxResidences ?? []), country: 'US', isDirty: true },
          ];
          handleValuesChange([
            { value, key },
            { value: newTaxResidences as ITaxResidenceCreate[], key: 'taxResidences' },
          ]);
        } else {
          handleValuesChange([{ value, key }]);
        }
      }

      // Remove US Tax residence added by the step above (with isDirty)
      if (value === '0') {
        const newTaxResidences = (taxResidences ?? [])?.filter(
          (taxResidence: ITaxResidenceCreate) => !(taxResidence.country === 'US' && taxResidence.isDirty),
        );
        handleValuesChange([
          { value, key },
          { value: newTaxResidences.length > 0 ? newTaxResidences : [defaultTaxResidence], key: 'taxResidences' },
        ]);
      }
    },
    [handleValuesChange, taxResidences],
  );

  return (
    <div>
      <BooleanRadio
        customLabel={
          <div className="flex gap-1 items-center">
            <span className="text-primary typo-body-medium-paragraph after:content-['_*'] after:text-error">
              {title}
            </span>
            <InfoTooltip
              tooltipInfo={
                <div className="flex flex-col items-start gap-3">
                  <span className="typo-body-regular-small">{description}</span>
                  <Button
                    variant="ghost"
                    color="inverse"
                    size="s"
                    as="a"
                    href="https://docs.treezor.com/guide/user-verification/tax-residence.html#the-specific-case-of-us-taxpayers"
                    openNewTab={true}
                    testId="btn-learn-more-us-resident"
                  >
                    {t('pages.dashboard.createUser.form.actions.learnMore')}
                  </Button>
                </div>
              }
              infoClassName="z-10 min-w-[240px]"
            />
          </div>
        }
        name="specifiedUSPerson"
        value={value}
        handleRadioChange={handleUSResidence}
        testId="radio-kyc-specified-us-person"
      />
      {shouldShowWarning && (
        <Helper type="warning" withIcon className="pt-3">
          {t('pages.dashboard.createUser.form.usTaxResidentWarning')}
        </Helper>
      )}
    </div>
  );
}
